/**
 * 创作流程**步骤指示器**(2026-07-25 抽取)—— 把页面里本来就存在的分段显式说出来。
 *
 * ═══ 为什么抽成共享件 ═══════════════════════════════════════════════════════════
 * 视频舱「创建 → 分镜 → 出片」与海报舱「备料 → 编排 → 出图」/「描述 → 出图 → 调整」
 * 是**同一个视觉件的三套标签**。视频侧那份把 3 个步骤名硬编在组件里,海报侧写的
 * 本来就是泛化版 —— 与其留两份,不如把泛化版收成共享件,各舱只提供标签与当前步。
 *
 * ═══ 两条纪律(两个舱一致,抽取时原样保留)═══════════════════════════════════════
 *  1. **零新状态**:`current` 必须由各舱 `model/*Step.ts` 从**已有** state 派生。
 *     它不驱动任何东西,只是读出来。
 *  2. **不可点**:点它跳不过去(没编排就没有分镜/区块可改),可点的假导航比没有更糟。
 *     故整条里不出现 button / a —— 两个舱的单测都锁这一条。
 *
 * `aria-current="step"` 不能省:纯视觉高亮对读屏用户等于没有。
 */
import { Check } from "lucide-react";

import { cn } from "@/shared/lib/utils";

export interface StepIndicatorProps {
  /** 步骤名(单源在各舱的 `model/*Step.ts`)。 */
  steps: readonly string[];
  /** 当前第几步,1-based。 */
  current: number;
  /** 无障碍名:说清是哪条管线的流程。 */
  label: string;
  /**
   * 各舱自己的 e2e 定位钩子属性名(如 `data-video-steps` / `data-poster-steps`),
   * 值 = 当前步。保留各自的名字,理由同 `SourceStrip.domainAttr`。
   */
  domainAttr?: string;
  className?: string;
}

export function StepIndicator({
  steps,
  current,
  label,
  domainAttr,
  className,
}: StepIndicatorProps) {
  return (
    <ol
      {...(domainAttr ? { [domainAttr]: String(current) } : {})}
      aria-label={label}
      className={cn("flex flex-wrap items-center gap-1.5 text-2xs", className)}
    >
      {steps.map((text, i) => {
        const step = i + 1;
        const done = step < current;
        const active = step === current;
        return (
          <li key={text} className="flex items-center gap-1.5">
            {i > 0 && (
              <span className="text-muted-foreground/50" aria-hidden>
                ›
              </span>
            )}
            <span
              aria-current={active ? "step" : undefined}
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-2 py-0.5",
                active && "bg-primary/10 font-semibold text-primary",
                done && "text-muted-foreground",
                !active && !done && "text-muted-foreground/70"
              )}
            >
              <span
                className={cn(
                  "grid h-4 w-4 shrink-0 place-items-center rounded-full text-2xs font-semibold",
                  active && "bg-primary text-primary-foreground",
                  done && "bg-muted text-muted-foreground",
                  !active && !done && "border border-border/70"
                )}
              >
                {done ? <Check className="h-2.5 w-2.5" /> : step}
              </span>
              {text}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
